import React from 'react';
import { Route, Routes } from 'react-router-dom';

import { SignIn } from '../modules/public/pages/signIn';
import { NewGym } from '../modules/public/pages/newGym';
import { ForgotPasswordStep1 } from '../modules/public/pages/forgotPasswordStep1';
import { ForgotPasswordStep2 } from '../modules/public/pages/forgotPasswordStep2';
import { ForgotPasswordStep3 } from '../modules/public/pages/forgotPasswordStep3';
import { SignUpVerify } from '../modules/public/pages/signUpVerify';
import { SignUpIdentity } from '../modules/public/pages/signUpIdentity';
import { AcceptTerms } from '../modules/public/pages/acceptTerms';

import { Dashboard } from '../modules/dashboard/pages/dashboard';
import { Exercises } from '../modules/exercises/pages/exercises';
import { ExerciseCreate } from '../modules/exercises/pages/exerciseCreate';
import { ExerciseDetails } from '../modules/exercises/pages/exerciseDetails';
import { GymProfile } from '../modules/gym/pages/gymProfile';
import { Profile } from '../modules/profile/pages/profile';
import { UserExercises } from '../modules/users/pages/userExercises';
import { TrainingTemplates } from '../modules/trainings/pages/trainingTemplates';
import { TrainingTemplateEditor } from '../modules/trainings/pages/trainingTemplateEditor';
import { UserDetails } from '../modules/users/pages/userDetails';
import { UserMeasurements } from '../modules/users/pages/userMeasurements';
import { UserDues } from '../modules/users/pages/userDues';
import { UserPermissions } from '../modules/users/pages/userPermissions';
import { Users } from '../modules/users/pages/users';
import { UserInvite } from '../modules/users/pages/userInvite';
import { StaffPermissions } from '../modules/users/pages/staffPermissions';
import { Warnings } from '../modules/gym/pages/warnings';
import { AuthRoute } from './Route';
import { Payments } from '../modules/payments/pages/payments';
import { Subscriptions } from '../modules/payments/pages/subscriptions';
import { Gyms } from '../modules/gym/pages/adminGyms';
import { GymDetails } from '../modules/gym/pages/gymDetails';
import { AdminUsers } from '../modules/admin/pages/users';
import { AdminPlans } from '../modules/admin/pages/plans';
import { GymFeatures } from '../modules/admin/pages/gymFeatures';
import { AdminUserDetail } from '../modules/admin/pages/userDetail';
import { ComingSoon } from '../modules/shared/pages/ComingSoon';
import { SupportMessages } from '../modules/admin/pages/supportMessages';
import { AccountDeletionRequests } from '../modules/admin/pages/accountDeletionRequests';
import { ChatInbox } from '../modules/chat/pages/inbox';
import { AdvancedReports } from '../modules/reports/pages/advancedReports';
import { PrintKiosk } from '../modules/users/pages/printKiosk';
import { OverdueStudents } from '../modules/dues/pages/overdue';
import { PhysicalAssessmentRequests } from '../modules/users/pages/physicalAssessmentRequests';
import { NotFoundPage } from '../components/NotFoundPage';
import { AdminExercises } from '../modules/admin/pages/exercises';
import { ExerciseEditor } from '../modules/admin/pages/exercises/exerciseEditor';
import { AdminAppVersions } from '../modules/admin/pages/appVersions';

const Router: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<SignIn />} />
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/new-gym" element={<NewGym />} />
      <Route path="/forgot-password" element={<ForgotPasswordStep1 />} />
      <Route path="/forgot-password/verify" element={<ForgotPasswordStep2 />} />
      <Route path="/forgot-password/reset" element={<ForgotPasswordStep3 />} />
      <Route path="/sign-up" element={<SignUpVerify />} />
      <Route path="/sign-up/identity" element={<SignUpIdentity />} />

      <Route
        path="/dashboard"
        element={
          <AuthRoute
            component={<Dashboard />}
            requiredFeature="dashboard_overview"
          />
        }
      />
      <Route
        path="/users"
        element={
          <AuthRoute
            component={<Users />}
            requiredPermission="view_gym_users"
          />
        }
      />
      <Route
        path="/users/new"
        element={
          <AuthRoute
            component={<UserInvite />}
            requiredPermission="manage_gym_users"
          />
        }
      />
      <Route
        path="/users/permissions"
        element={
          <AuthRoute
            component={<StaffPermissions />}
            requiredPermission="manage_gym_users"
            requiredFeature="user_permission_management"
          />
        }
      />
      <Route
        path="/users/:id"
        element={
          <AuthRoute
            component={<UserDetails />}
            requiredPermission="view_gym_users"
          />
        }
      />
      <Route
        path="/users/:id/:day"
        element={
          <AuthRoute
            component={<UserExercises />}
            requiredPermission="manage_trainings"
            requiredFeature="training_management"
          />
        }
      />
      <Route
        path="/users/:id/measurements"
        element={
          <AuthRoute
            component={<UserMeasurements />}
            requiredPermission="manage_measurements"
            requiredFeature="student_metrics"
          />
        }
      />
      <Route
        path="/users/:id/dues"
        element={
          <AuthRoute
            component={<UserDues />}
            requiredPermission="manage_student_billing"
            requiredFeature="student_billing"
          />
        }
      />
      <Route
        path="/users/:id/permissions"
        element={
          <AuthRoute
            component={<UserPermissions />}
            requiredPermission="manage_gym_users"
            requiredFeature="user_permission_management"
          />
        }
      />
      <Route
        path="/trainings"
        element={
          <AuthRoute
            component={<Users variant="trainings" />}
            requiredPermission="manage_trainings"
            requiredFeature="training_management"
          />
        }
      />
      <Route
        path="/trainings/:id"
        element={
          <AuthRoute
            component={<UserExercises variant="trainings" />}
            requiredPermission="manage_trainings"
            requiredFeature="training_management"
          />
        }
      />
      <Route
        path="/trainings/:id/:day"
        element={
          <AuthRoute
            component={<UserExercises variant="trainings" />}
            requiredPermission="manage_trainings"
            requiredFeature="training_management"
          />
        }
      />
      <Route
        path="/trainings/templates"
        element={
          <AuthRoute
            component={<TrainingTemplates />}
            requiredPermission="manage_training_templates"
            requiredFeature="training_templates"
          />
        }
      />
      <Route
        path="/trainings/templates/new"
        element={
          <AuthRoute
            component={<TrainingTemplateEditor />}
            requiredPermission="manage_training_templates"
            requiredFeature="training_templates"
          />
        }
      />
      <Route
        path="/trainings/templates/:id"
        element={
          <AuthRoute
            component={<TrainingTemplateEditor />}
            requiredPermission="manage_training_templates"
            requiredFeature="training_templates"
          />
        }
      />
      <Route
        path="/exercises"
        element={
          <AuthRoute
            component={<Exercises />}
            requiredPermission="view_exercises"
          />
        }
      />
      <Route
        path="/exercises/new"
        element={
          <AuthRoute
            component={<ExerciseCreate />}
            requiredPermission="manage_exercises"
          />
        }
      />
      <Route
        path="/exercises/:id"
        element={
          <AuthRoute
            component={<ExerciseDetails />}
            requiredPermission="manage_exercises"
          />
        }
      />
      <Route
        path="/warnings"
        element={
          <AuthRoute
            component={<Warnings />}
            requiredPermission="manage_news"
            requiredFeature="announcements"
          />
        }
      />
      <Route
        path="/gym/profile"
        element={
          <AuthRoute
            component={<GymProfile />}
            requiredPermission="manage_gym_profile"
          />
        }
      />
      <Route
        path="/reports"
        element={
          <AuthRoute
            component={<AdvancedReports />}
            requiredPermission="view_gym_dashboard"
          />
        }
      />
      <Route
        path="/users/overdue"
        element={
          <AuthRoute
            component={<OverdueStudents />}
            requiredPermission="manage_student_billing"
            requiredFeature="overdue_alert"
          />
        }
      />
      <Route
        path="/users/physical-assessment"
        element={
          <AuthRoute
            component={<PhysicalAssessmentRequests />}
            requiredPermission="manage_measurements"
            requiredFeature="student_metrics"
          />
        }
      />
      <Route
        path="/print-kiosk"
        element={
          <AuthRoute
            component={<PrintKiosk />}
            requiredPermission="manage_trainings"
            requiredFeature="print_kiosk"
          />
        }
      />
      <Route
        path="/support"
        element={
          <AuthRoute
            component={<ChatInbox />}
            requiredPermission="manage_chat"
            requiredFeature="chat"
          />
        }
      />
      <Route
        path="/gym/payments"
        element={
          <AuthRoute
            component={<Payments />}
            requiredPermission="manage_payments"
          />
        }
      />
      <Route
        path="/gym/subscriptions"
        element={
          <AuthRoute
            component={<Subscriptions />}
            requiredPermission="manage_subscriptions"
          />
        }
      />
      <Route
        path="/admin/gyms"
        element={
          <AuthRoute
            component={<Gyms />}
            requiredPermission="manage_platform_gyms"
          />
        }
      />
      <Route
        path="/admin/gyms/:gym_id"
        element={
          <AuthRoute
            component={<GymDetails />}
            requiredPermission="manage_platform_gyms"
          />
        }
      />
      <Route
        path="/admin/support"
        element={
          <AuthRoute
            component={<SupportMessages />}
            requiredPermission="manage_platform_gyms"
          />
        }
      />
      <Route
        path="/admin/support/account-deletions"
        element={
          <AuthRoute
            component={<AccountDeletionRequests />}
            requiredPermission="manage_platform_gyms"
          />
        }
      />
      <Route
        path="/admin/users"
        element={
          <AuthRoute
            component={<AdminUsers />}
            requiredPermission="manage_platform_gyms"
          />
        }
      />
      <Route
        path="/admin/exercises"
        element={
          <AuthRoute
            component={<AdminExercises />}
            requiredPermission="manage_platform_gyms"
          />
        }
      />
      <Route
        path="/admin/exercises/new"
        element={
          <AuthRoute
            component={<ExerciseEditor />}
            requiredPermission="manage_platform_gyms"
          />
        }
      />
      <Route
        path="/admin/exercises/:id"
        element={
          <AuthRoute
            component={<ExerciseEditor />}
            requiredPermission="manage_platform_gyms"
          />
        }
      />
      <Route
        path="/admin/users/:user_id"
        element={
          <AuthRoute
            component={<AdminUserDetail />}
            requiredPermission="manage_platform_gyms"
          />
        }
      />
      <Route
        path="/admin/plans"
        element={
          <AuthRoute
            component={<AdminPlans />}
            requiredPermission="manage_platform_gyms"
          />
        }
      />
      <Route
        path="/admin/gyms/:gym_id/features"
        element={
          <AuthRoute
            component={<GymFeatures />}
            requiredPermission="manage_platform_gyms"
          />
        }
      />
      <Route
        path="/admin/billing"
        element={
          <AuthRoute
            component={<ComingSoon title="Assinaturas & Faturamento" />}
            requiredPermission="manage_platform_gyms"
          />
        }
      />
      <Route
        path="/admin/reports"
        element={
          <AuthRoute
            component={<ComingSoon title="Relatórios globais" />}
            requiredPermission="manage_platform_gyms"
          />
        }
      />
      <Route
        path="/admin/app-versions"
        element={
          <AuthRoute
            component={<AdminAppVersions />}
            requiredPermission="manage_platform_gyms"
          />
        }
      />
      <Route
        path="/admin/settings"
        element={
          <AuthRoute
            component={<ComingSoon title="Config. do sistema" />}
            requiredPermission="manage_platform_gyms"
          />
        }
      />
      <Route path="/profile" element={<AuthRoute component={<Profile />} />} />
      <Route
        path="/accept-terms"
        element={<AuthRoute component={<AcceptTerms />} />}
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export { Router };
